/* eslint-disable import/prefer-default-export */
import { identity, isEmpty, pickBy } from 'lodash'
import type { APIResponse } from '..'
import { FETCH_STATUS } from '@src/constants/events'
import type { FilterState } from '@src/redux/reducers/filter'
import GlobalEvents from '@src/utils/custom.event'
import { jsonify, toastError } from '@src/utils/toast'
export * from './to.query.string'

export const paramsWithFilter = <T>(
  params: T,
  filterState: FilterState,
  defaultOptions?: Partial<T & { account_id: string }>
) =>
  pickBy(
    {
      ...defaultOptions,
      ...params,
      account_id: filterState?.currentAccount?.id || defaultOptions?.account_id,
      account_uid: filterState?.currentAccount?.uid
    },
    identity
  )

export const getErrorResponse = <T>(res: APIResponse<T>) => {
  const {
    error,
    message,
    code,
    messages,
    detailCode,
    errorMessage,
    trackingId
  } = (res || {}) as APIResponse<T>
  const hasError =
    (res && error && code) ||
    (errorMessage && trackingId) ||
    (res && error && message)
  const hasErrorMessage =
    (res && !isEmpty(message) && detailCode) || errorMessage
  const hasErrorMessages = res && !isEmpty(messages) && detailCode
  if (hasError || hasErrorMessage || hasErrorMessages) {
    const msg = jsonify(
      message ||
        error ||
        (messages || []).map(msq => msq.text).join('\n') ||
        errorMessage
    )
    return msg
  }
  return null
}

export function defaultHandleStatus<T>(
  requestResult: APIResponse<T>,
  actionError?: (data?: string) => void,
  showToastMessage?: boolean,
  dispatch?: (data) => void
) {
  const errorResponse = getErrorResponse(requestResult)
  if (!isEmpty(errorResponse)) {
    if (dispatch && actionError) {
      dispatch(actionError(errorResponse))
    } else if (actionError) {
      actionError(errorResponse)
    }
    if (showToastMessage) {
      toastError(errorResponse)
    }
    return Promise.reject(requestResult)
  }
  return Promise.resolve(requestResult)
}

function defaultHandleResponseErrorsMessage(response: Response) {
  if (response?.status >= 400) {
    showErrorMessage(response)
  }
}

export const handleErrors =
  (
    errorHandler?: () => void,
    handleResponseErrorsMessage = defaultHandleResponseErrorsMessage
  ) =>
  (response: Response) => {
    if (!response?.ok) {
      errorHandler()
      GlobalEvents.dispatch(FETCH_STATUS, { status: 'requestError' })
      switch (response?.status) {
        case 502:
          toastError(`${response?.statusText}`)
          break

        default:
          handleResponseErrorsMessage(response)
          break
      }
    }
    return response
  }

export const showErrorMessage = error => {
  if (error) {
    if (error.error) {
      toastError(error.error)
    } else if (error.message) {
      toastError(error.message)
    } else if (error.statusText) {
      toastError(error.statusText)
    }
  }
}
const checkByType = <T>(val: T) => {
  switch (typeof val) {
    case 'boolean':
    case 'number':
      return val !== null || val !== undefined
    default: {
      return !isEmpty(val)
    }
  }
}

const cleanEmpty = obj => {
  if (Array.isArray(obj)) {
    return obj
      .map(v => (v && typeof v === 'object' ? cleanEmpty(v) : v))
      .filter(v => checkByType)
  } else {
    return Object.entries(obj)
      .map(([k, v]) => [k, v && typeof v === 'object' ? cleanEmpty(v) : v])
      .reduce((a, [k, v]) => (!checkByType(v) ? a : ((a[k] = v), a)), {})
  }
}

export const preProcessingResponse = <T>(response: T): Promise<T> =>
  Promise.resolve<T>(cleanEmpty(response))

export async function handleBlob(res: Response): Promise<{
  file: Blob
  filename: string
}> {
  try {
    if (!res.ok) {
      return Promise.reject(res)
    }
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const disposition = res.headers.get('Content-Disposition')
    const matches = filenameRegex.exec(disposition)
    let filename = ''
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '')
    }

    const blob = await res.blob().catch(ex => Promise.reject())
    return { file: blob, filename }
  } catch (ex) {
    return Promise.reject(res)
  }
}
